package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.MonthlyFinancials
import br.com.vvrefeicoes.portal.domain.model.UnitMargin
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalPeriod
import com.composables.icons.lucide.BadgePercent
import com.composables.icons.lucide.Banknote
import com.composables.icons.lucide.ChartColumn
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Scale
import com.composables.icons.lucide.TrendingDown
import com.composables.icons.lucide.TrendingUp
import com.composables.icons.lucide.Truck
import com.composables.icons.lucide.Users
import io.github.koalaplot.core.bar.DefaultBarPlotGroupedPointEntry
import io.github.koalaplot.core.bar.DefaultBarPosition
import io.github.koalaplot.core.bar.GroupedVerticalBarPlot
import io.github.koalaplot.core.bar.HorizontalBarPlot
import io.github.koalaplot.core.line.LinePlot
import io.github.koalaplot.core.style.LineStyle
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.FloatLinearAxisModel
import io.github.koalaplot.core.xygraph.Point
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_overview_employees
import portalvv.shared.generated.resources.operational_overview_empty
import portalvv.shared.generated.resources.operational_overview_expense
import portalvv.shared.generated.resources.operational_overview_expense_planned
import portalvv.shared.generated.resources.operational_overview_fopag
import portalvv.shared.generated.resources.operational_overview_fopag_percent
import portalvv.shared.generated.resources.operational_overview_legend_expense
import portalvv.shared.generated.resources.operational_overview_legend_revenue
import portalvv.shared.generated.resources.operational_overview_monthly_results_title
import portalvv.shared.generated.resources.operational_overview_profitability_axis
import portalvv.shared.generated.resources.operational_overview_profitability_title
import portalvv.shared.generated.resources.operational_overview_result
import portalvv.shared.generated.resources.operational_overview_result_provisioned
import portalvv.shared.generated.resources.operational_overview_revenue
import portalvv.shared.generated.resources.operational_overview_revenue_expense_axis
import portalvv.shared.generated.resources.operational_overview_revenue_expense_title
import portalvv.shared.generated.resources.operational_overview_revenue_per_fopag
import portalvv.shared.generated.resources.operational_overview_revenue_planned
import portalvv.shared.generated.resources.operational_overview_transported
import portalvv.shared.generated.resources.operational_overview_units_margin_axis
import portalvv.shared.generated.resources.operational_overview_units_negative_title
import portalvv.shared.generated.resources.operational_overview_units_title
import kotlin.math.abs
import kotlin.math.ceil
import kotlin.math.round
import kotlin.math.roundToLong

private val TILE_HEIGHT = 88.dp

private val SIDE_BY_SIDE_MIN_WIDTH = 840.dp

/** Espessura fixa das barras horizontais dos gráficos de margem, igual para ambos os cards. */
private val MARGIN_BAR_THICKNESS = 26.dp

private val MONTHLY_RESULT_SIX_COLUMNS_MIN_WIDTH = 1400.dp
private val MONTHLY_RESULT_FOUR_COLUMNS_MIN_WIDTH = 1080.dp
private val MONTHLY_RESULT_THREE_COLUMNS_MIN_WIDTH = 760.dp
private val MONTHLY_RESULT_TWO_COLUMNS_MIN_WIDTH = 520.dp

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun OverviewContent() {
    val controller = rememberOverviewController()
    val filters = LocalGlobalFilters.current
    val period = LocalGlobalPeriod.current

    LaunchedEffect(controller) {
        controller.init()
    }

    LaunchedEffect(period.selection) {
        controller.applyPeriod(period.selection)
    }

    LaunchedEffect(filters.unit, filters.manager, filters.director, filters.units) {
        controller.applyFilter(filters.selectedUnit, filters.selectedManager, filters.selectedDirector, filters.units)
    }

    if (controller.loading) {
        Box(
            modifier = Modifier.fillMaxWidth().height(200.dp),
            contentAlignment = Alignment.Center,
        ) {
            CircularProgressIndicator()
        }
        return
    }

    if (controller.units.isEmpty()) {
        EmptyState(message = stringResource(Res.string.operational_overview_empty))
        return
    }

    val metrics = controller.metrics
    val resultPercent = if (metrics.revenueTotalCents == 0L) 0.0 else metrics.resultCents.toDouble() / metrics.revenueTotalCents * 100
    val provisionedResultCents = metrics.revenuePlannedCents - metrics.expensePlannedCents
    val provisionedResultPercent =
        if (metrics.revenuePlannedCents == 0L) 0.0 else provisionedResultCents.toDouble() / metrics.revenuePlannedCents * 100
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        maxItemsInEachRow = 4,
    ) {
        KpiTile(
            icon = Lucide.TrendingUp,
            iconColor = AppPalette.Success500,
            iconContainerColor = AppPalette.Success100,
            label = stringResource(Res.string.operational_overview_revenue),
            value = money(metrics.revenueTotalCents),
            secondary = stringResource(Res.string.operational_overview_revenue_planned, money(metrics.revenuePlannedCents)),
        )
        KpiTile(
            icon = Lucide.TrendingDown,
            iconColor = AppPalette.Error500,
            iconContainerColor = AppPalette.Error100,
            label = stringResource(Res.string.operational_overview_expense),
            value = money(metrics.expenseTotalCents),
            secondary = stringResource(Res.string.operational_overview_expense_planned, money(metrics.expensePlannedCents)),
        )
        KpiTile(
            icon = Lucide.Scale,
            iconColor = if (metrics.resultCents < 0) AppPalette.Error500 else AppPalette.Success500,
            iconContainerColor = if (metrics.resultCents < 0) AppPalette.Error100 else AppPalette.Success100,
            label = stringResource(Res.string.operational_overview_result),
            value = "${money(metrics.resultCents)} (${percent(resultPercent)})",
            secondary =
                stringResource(
                    Res.string.operational_overview_result_provisioned,
                    "${money(provisionedResultCents)} (${percent(provisionedResultPercent)})",
                ),
            valueColor = if (metrics.resultCents < 0) AppPalette.Error500 else AppPalette.Success500,
        )
        KpiTile(
            icon = Lucide.Banknote,
            iconColor = AppPalette.Primary500,
            iconContainerColor = AppPalette.Primary100,
            label = stringResource(Res.string.operational_overview_fopag),
            value = money(metrics.fopagCents),
        )
        KpiTile(
            icon = Lucide.Truck,
            iconColor = AppPalette.Info500,
            iconContainerColor = AppPalette.Info100,
            label = stringResource(Res.string.operational_overview_transported),
            value = money(metrics.transportedCents),
        )
        KpiTile(
            icon = Lucide.BadgePercent,
            iconColor = AppPalette.Warning500,
            iconContainerColor = AppPalette.Warning100,
            label = stringResource(Res.string.operational_overview_fopag_percent),
            value = percent(metrics.fopagPercent),
        )
        KpiTile(
            icon = Lucide.ChartColumn,
            iconColor = AppPalette.Primary500,
            iconContainerColor = AppPalette.Primary100,
            label = stringResource(Res.string.operational_overview_revenue_per_fopag),
            value = ratio(metrics.revenuePerFopag),
        )
        KpiTile(
            icon = Lucide.Users,
            iconColor = AppPalette.Accent500,
            iconContainerColor = AppPalette.Accent100,
            label = stringResource(Res.string.operational_overview_employees),
            value = metrics.headcount.toString(),
        )
    }

    ProfitabilityChartCard(series = controller.monthlyFinancials)
    RevenueExpenseChartCard(series = controller.monthlyFinancials)
    MonthlyResultCards(series = controller.monthlyFinancials)
    val aboveTargetCard: @Composable (Modifier) -> Unit = { modifier ->
        UnitMarginChartCard(
            title = stringResource(Res.string.operational_overview_units_title),
            series = controller.unitMargins,
            barColor = AppPalette.Success500,
            modifier = modifier,
        )
    }
    val negativeCard: @Composable (Modifier) -> Unit = { modifier ->
        UnitMarginChartCard(
            title = stringResource(Res.string.operational_overview_units_negative_title),
            series = controller.negativeUnitMargins,
            barColor = AppPalette.Error500,
            modifier = modifier,
        )
    }
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        if (maxWidth >= SIDE_BY_SIDE_MIN_WIDTH) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
            ) {
                aboveTargetCard(Modifier.weight(1f))
                negativeCard(Modifier.weight(1f))
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                aboveTargetCard(Modifier.fillMaxWidth())
                negativeCard(Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
private fun ProfitabilityChartCard(series: List<MonthlyFinancials>) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = stringResource(Res.string.operational_overview_profitability_title),
                style = MaterialTheme.typography.titleMedium,
            )
            if (series.isEmpty()) {
                Text(
                    text = "—",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                val labels = series.map { shortMonthLabel(it.period) }
                val percents = series.map { it.profitPercent.toFloat() }
                val maxPercent = percents.maxOrNull() ?: 0f
                val minPercent = percents.minOrNull() ?: 0f
                val span = (maxPercent - minPercent).let { if (it > 0f) it else 1f }
                val pad = span * 0.25f
                val yAxis =
                    if (minPercent < 0f) {
                        rememberSignedValueAxis(minPercent - pad, maxPercent + pad)
                    } else {
                        rememberValueAxis(maxPercent.coerceAtLeast(1f), divisions = 4, headroom = 1.5f)
                    }
                val points = series.mapIndexed { index, item -> Point(labels[index], item.profitPercent.toFloat()) }
                XYGraph(
                    gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                    xAxisModel = CategoryAxisModel(labels),
                    yAxisModel = yAxis,
                    xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                    yAxisContent =
                        rememberAxisContent(
                            title = { Text(stringResource(Res.string.operational_overview_profitability_axis), style = MaterialTheme.typography.labelSmall) },
                            labels = { value -> Text(percent(value.toDouble()), style = MaterialTheme.typography.labelSmall) },
                        ),
                    modifier = Modifier.fillMaxWidth().height(240.dp),
                ) {
                    LinePlot(
                        data = points,
                        lineStyle = LineStyle(brush = SolidColor(AppPalette.Primary500), strokeWidth = 2.dp),
                        symbol = { point ->
                            HoverTooltip(
                                text = percent(point.y.toDouble()),
                                textStyle = MaterialTheme.typography.titleMedium,
                            ) {
                                LinePointLabel(percent(point.y.toDouble()), AppPalette.Primary500)
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun RevenueExpenseChartCard(series: List<MonthlyFinancials>) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = stringResource(Res.string.operational_overview_revenue_expense_title),
                style = MaterialTheme.typography.titleMedium,
            )
            SeriesLegend(
                entries =
                    listOf(
                        LegendEntry(AppPalette.Success500, stringResource(Res.string.operational_overview_legend_revenue)),
                        LegendEntry(AppPalette.Error500, stringResource(Res.string.operational_overview_legend_expense)),
                    ),
            )
            if (series.isEmpty()) {
                Text(
                    text = "—",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                val labels = series.map { shortMonthLabel(it.period) }
                val maxValue = series.maxOf { maxOf(it.revenueCents, it.expenseCents) }.coerceAtLeast(1L).toFloat() / 100f
                val data =
                    series.mapIndexed { index, item ->
                        DefaultBarPlotGroupedPointEntry(
                            i = labels[index],
                            d =
                                listOf(
                                    DefaultBarPosition(0f, item.revenueCents.toFloat() / 100f),
                                    DefaultBarPosition(0f, item.expenseCents.toFloat() / 100f),
                                ),
                        )
                    }
                XYGraph(
                    gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                    xAxisModel = CategoryAxisModel(labels),
                    yAxisModel = rememberValueAxis(maxValue, headroom = 1.2f),
                    xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                    yAxisContent =
                        rememberAxisContent(
                            title = { Text(stringResource(Res.string.operational_overview_revenue_expense_axis), style = MaterialTheme.typography.labelSmall) },
                            labels = { value -> Text(money((value * 100).roundToLong()), style = MaterialTheme.typography.labelSmall) },
                        ),
                    modifier = Modifier.fillMaxWidth().height(280.dp),
                ) {
                    GroupedVerticalBarPlot(
                        data = data,
                        maxBarGroupWidth = 0.6f,
                        bar = { _, seriesIndex, value ->
                            val revenue = value.d.getOrNull(0)?.end
                            val expense = value.d.getOrNull(1)?.end
                            val revenueLabel = stringResource(Res.string.operational_overview_legend_revenue)
                            val expenseLabel = stringResource(Res.string.operational_overview_legend_expense)
                            val tooltip =
                                buildString {
                                    append(value.i)
                                    if (revenue != null) {
                                        append('\n')
                                        append(revenueLabel)
                                        append(": ")
                                        append(money((revenue * 100).roundToLong()))
                                    }
                                    if (expense != null) {
                                        append('\n')
                                        append(expenseLabel)
                                        append(": ")
                                        append(money((expense * 100).roundToLong()))
                                    }
                                }
                            HoverTooltip(text = tooltip) {
                                ValueBar(
                                    color = if (seriesIndex == 0) AppPalette.Success500 else AppPalette.Error500,
                                    label = compactMoney(((value.d.getOrNull(seriesIndex)?.end ?: 0f) * 100).roundToLong()),
                                    horizontal = false,
                                )
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun MonthlyResultCards(series: List<MonthlyFinancials>) {
    if (series.isEmpty()) return
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val columns =
            when {
                maxWidth >= MONTHLY_RESULT_SIX_COLUMNS_MIN_WIDTH -> 6
                maxWidth >= MONTHLY_RESULT_FOUR_COLUMNS_MIN_WIDTH -> 4
                maxWidth >= MONTHLY_RESULT_THREE_COLUMNS_MIN_WIDTH -> 3
                maxWidth >= MONTHLY_RESULT_TWO_COLUMNS_MIN_WIDTH -> 2
                else -> 1
            }
        // Um card por mês, do mais recente para o mais antigo.
        val ordered = series.sortedByDescending { it.period.id }
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
            Text(
                text = stringResource(Res.string.operational_overview_monthly_results_title),
                style = MaterialTheme.typography.titleMedium,
            )
            ordered.chunked(columns).forEach { rowMonths ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                    verticalAlignment = Alignment.Top,
                ) {
                    rowMonths.forEach { month ->
                        MonthlyResultCard(month = month, modifier = Modifier.weight(1f))
                    }
                    repeat(columns - rowMonths.size) {
                        Spacer(modifier = Modifier.weight(1f))
                    }
                }
            }
        }
    }
}

@Composable
private fun MonthlyResultCard(
    month: MonthlyFinancials,
    modifier: Modifier = Modifier,
) {
    val color = if (month.profitPercent < 0) AppPalette.Error500 else AppPalette.Success500
    Surface(
        modifier = modifier,
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
        ) {
            Text(
                text = MonthCalendar.label(month.period),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = percent(month.profitPercent),
                style = MaterialTheme.typography.headlineSmall,
                color = color,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = money(month.resultCents),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun UnitMarginChartCard(
    title: String,
    series: List<UnitMargin>,
    barColor: Color,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
            )
            if (series.isEmpty()) {
                Text(
                    text = "—",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                val chartData = series.reversed()
                val names = chartData.map { it.name }
                val values = chartData.map { it.marginPercent.toFloat() }
                val isNegative = values.all { it <= 0f }
                val xMax = ((ceil(values.maxOf { abs(it) }.toDouble() / 5.0) * 5.0).coerceAtLeast(10.0) * 1.2).toFloat()
                val range = if (isNegative) -xMax..0f else 0f..xMax
                val barThicknessPx = with(LocalDensity.current) { MARGIN_BAR_THICKNESS.toPx() }
                var plotHeight by remember { mutableStateOf(0) }
                val barWidth =
                    if (plotHeight > 0) {
                        (barThicknessPx * (chartData.size + 1) / plotHeight).coerceIn(0.05f, 1f)
                    } else {
                        0.6f
                    }
                XYGraph(
                    gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                    xAxisModel = FloatLinearAxisModel(range),
                    yAxisModel = CategoryAxisModel(names),
                    xAxisContent =
                        rememberAxisContent(
                            title = { Text(stringResource(Res.string.operational_overview_units_margin_axis), style = MaterialTheme.typography.labelSmall) },
                            labels = { value -> Text(percent(value.toDouble()), style = MaterialTheme.typography.labelSmall) },
                        ),
                    yAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                    modifier = Modifier.fillMaxWidth().height((chartData.size * 44 + 48).dp),
                ) {
                    Box(Modifier.fillMaxSize().onSizeChanged { plotHeight = it.height })
                    HorizontalBarPlot(
                        xData = values,
                        yData = names,
                        barWidth = barWidth,
                        bar = { _, _, value ->
                            HoverTooltip(text = "${value.y} — ${percent(value.x.end.toDouble())}") {
                                ValueBar(
                                    color = barColor,
                                    label = percent(value.x.end.toDouble()),
                                    horizontal = true,
                                    negative = value.x.end < 0f,
                                )
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun FlowRowScope.KpiTile(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    secondary: String? = null,
    valueColor: Color = MaterialTheme.colorScheme.onSurface,
) {
    Surface(
        modifier = modifier.weight(1f).height(TILE_HEIGHT),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Surface(
                shape = AppShapes.small,
                color = iconContainerColor,
                modifier = Modifier.size(32.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = iconColor,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.titleMedium,
                    color = valueColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                secondary?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

private fun money(cents: Long): String = "R$ ${MoneyFormatter.formatCents(cents)}"

private fun percent(value: Double): String {
    val scaled = round(value * 100).toLong()
    return "${scaled / 100},${abs(scaled % 100).toString().padStart(2, '0')}%"
}

private fun ratio(value: Double): String {
    val scaled = round(value * 100).toLong()
    return "${scaled / 100},${abs(scaled % 100).toString().padStart(2, '0')}x"
}

private val shortMonths = listOf("Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez")

private fun shortMonthLabel(period: MonthPeriod): String = "${shortMonths[period.month - 1]}/${(period.year % 100).toString().padStart(2, '0')}"
